import { Controller } from "@hotwired/stimulus";

// Editor for EtudeShowLayout per role.
// State lives in a hidden modules_json field per role-form. The DOM list is
// the visual representation; on every mutation we re-serialize to JSON so the
// classic form submit POSTs the right payload — no fancy AJAX needed.
export default class extends Controller {
  static targets = ["roleTab", "rolePanel", "form", "modulesField", "modulesList"];

  connect() {
    // Cache: per role, the in-memory list of modules currently composed.
    this._state = {};
    this.formTargets.forEach((form) => {
      const role = form.dataset.role;
      const field = form.querySelector("[data-etude-view-editor-target='modulesField']");
      try {
        this._state[role] = JSON.parse(field.value || "[]");
      } catch (e) {
        this._state[role] = [];
      }
    });
  }

  switchRole(event) {
    const targetRole = event.currentTarget.dataset.role;
    this.roleTabTargets.forEach((t) =>
      t.classList.toggle("esv_role-tab--active", t.dataset.role === targetRole)
    );
    this.rolePanelTargets.forEach((p) =>
      p.classList.toggle("esv_role-panel--active", p.dataset.role === targetRole)
    );
  }

  addModule(event) {
    const role = this._currentRole();
    const item = event.currentTarget;
    const key = item.dataset.moduleKey;
    let defaults = {};
    try {
      defaults = JSON.parse(item.dataset.moduleDefault || "{}");
    } catch (e) {
      defaults = {};
    }
    this._state[role].push({ type: key, config: defaults });
    this._rerender(role);
  }

  remove(event) {
    const role = this._currentRole();
    const li = event.currentTarget.closest("li");
    const idx = Array.from(li.parentElement.children).indexOf(li);
    this._state[role].splice(idx, 1);
    this._rerender(role);
  }

  moveUp(event) {
    const role = this._currentRole();
    const li = event.currentTarget.closest("li");
    const idx = Array.from(li.parentElement.children).indexOf(li);
    if (idx <= 0) return;
    const arr = this._state[role];
    [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
    this._rerender(role);
  }

  moveDown(event) {
    const role = this._currentRole();
    const li = event.currentTarget.closest("li");
    const idx = Array.from(li.parentElement.children).indexOf(li);
    const arr = this._state[role];
    if (idx >= arr.length - 1) return;
    [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
    this._rerender(role);
  }

  configChanged(event) {
    const role = this._currentRole();
    const input = event.currentTarget;
    const li = input.closest("li[data-module-key]");
    if (!li) return;
    const list = li.parentElement;
    const idx = Array.from(list.children).indexOf(li);
    const fieldKey = input.dataset.field;
    const fieldType = input.dataset.type;
    const entry = this._state[role][idx];
    if (!entry) return;
    entry.config = entry.config || {};

    if (fieldType === "boolean") {
      entry.config[fieldKey] = input.checked;
    } else if (fieldType === "number") {
      entry.config[fieldKey] = Number(input.value);
    } else if (fieldType === "multi_select") {
      const fieldDiv = input.closest(".esv_field");
      const checkboxes = fieldDiv.querySelectorAll("input[type='checkbox']");
      const selected = Array.from(checkboxes).filter((c) => c.checked).map((c) => c.value);
      entry.config[fieldKey] = selected;
      input.closest(".esv_chip-toggle")?.classList.toggle("esv_chip-toggle--on", input.checked);
    } else {
      entry.config[fieldKey] = input.value;
    }
    this._sync(role);
  }

  _currentRole() {
    const activePanel = this.rolePanelTargets.find((p) =>
      p.classList.contains("esv_role-panel--active")
    );
    return activePanel?.dataset.role;
  }

  _activeForm(role) {
    return this.formTargets.find((f) => f.dataset.role === role);
  }

  _activePanel(role) {
    return this.rolePanelTargets.find((p) => p.dataset.role === role);
  }

  _sync(role) {
    const form = this._activeForm(role);
    const field = form.querySelector("[data-etude-view-editor-target='modulesField']");
    field.value = JSON.stringify(this._state[role]);
  }

  // Re-rendering after add/remove/reorder requires a server round-trip to
  // re-render the per-module config form (we don't duplicate that schema in
  // JS). To avoid that complexity, we sync the field and simply refresh the
  // visible <li> list in DOM order using clones. Config inputs are preserved
  // through the same DOM nodes; new entries get a placeholder requiring save+reload.
  _rerender(role) {
    this._sync(role);
    const panel = this._activePanel(role);
    const list = panel.querySelector("[data-etude-view-editor-target='modulesList']");
    const existingByKey = {};
    list.querySelectorAll("li[data-module-key]").forEach((li) => {
      existingByKey[li.dataset.moduleKey] = li;
    });

    list.innerHTML = "";
    this._state[role].forEach((entry, i) => {
      const existing = existingByKey[entry.type];
      if (existing && !list.contains(existing)) {
        list.appendChild(existing);
      } else if (!existing) {
        const placeholder = document.createElement("li");
        placeholder.className = "esv_module esv_module--pending";
        placeholder.dataset.moduleKey = entry.type;
        placeholder.innerHTML = `
          <header class="esv_module__head">
            <i class="fa-solid fa-circle-plus"></i>
            <span class="esv_module__title">${entry.type}</span>
            <div class="esv_module__controls">
              <button type="button" class="esv_btn-icon" data-action="click->etude-view-editor#moveUp" title="Monter"><i class="fa-solid fa-arrow-up"></i></button>
              <button type="button" class="esv_btn-icon" data-action="click->etude-view-editor#moveDown" title="Descendre"><i class="fa-solid fa-arrow-down"></i></button>
              <button type="button" class="esv_btn-icon esv_btn-icon--danger" data-action="click->etude-view-editor#remove" title="Retirer"><i class="fa-solid fa-trash"></i></button>
            </div>
          </header>
          <div class="esv_module__no-config">Module ajouté · enregistre puis recharge pour voir ses paramètres.</div>
        `;
        list.appendChild(placeholder);
      }
    });

    const empty = panel.querySelector(".esv_canvas__empty");
    if (empty) empty.style.display = this._state[role].length ? "none" : "";
  }
}
